import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import { Cancel, CheckCircle, Error as ErrorIcon, Warning } from '@mui/icons-material';
import MDBox from '../../controls/MDBox';
import MDTypography from '../../controls/MDTypography';
import MDButton from '../../controls/MDButton';
import MDBadge from '../../controls/MDBadge';
import { postData } from '../../Utils/Http/api';

// «FechaCambioPass» es la fecha en que vence la contraseña: pasada esa fecha, el login obliga a cambiarla.
// Rojo: vencida o con menos de una semana. Amarillo: con menos de 15 días.
const DIAS_URGENTE = 7;
const DIAS_AVISO = 15;
const COLOR_ROJO = '#d32f2f';
const COLOR_AMARILLO = '#f9a825';
const COLOR_AMARILLO_TEXTO = '#b26a00';

const inicioDelDia = (fecha) => new Date(fecha.getFullYear(), fecha.getMonth(), fecha.getDate());

// Días de calendario entre hoy y el vencimiento (negativo si ya venció).
const diasParaVencer = (fecha, ahora) => Math.round((inicioDelDia(fecha) - inicioDelDia(ahora)) / 86400000);

const nivelDe = (dias) => (dias < DIAS_URGENTE ? 'urgente' : dias < DIAS_AVISO ? 'aviso' : null);

const plural = (n) => `${n} ${n === 1 ? 'día' : 'días'}`;

const textoVencimiento = (fecha, dias, ahora) => {
  if (fecha < ahora) return dias < 0 ? `Vencida hace ${plural(-dias)}` : 'Vencida hoy';
  return dias === 0 ? 'Vence hoy' : `Faltan ${plural(dias)}`;
};

const formatearFecha = (fecha) =>
  `${String(fecha.getDate()).padStart(2, '0')}/${String(fecha.getMonth() + 1).padStart(2, '0')}/${fecha.getFullYear()}`;

const colorIntentos = (intentos) => (intentos <= 2 ? 'success' : intentos <= 4 ? 'warning' : 'error');

function UsuarioVencimientoClaves({ open, onClose }) {
  const [usuarios, setUsuarios] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');
  const [soloConAviso, setSoloConAviso] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let cancelado = false;
    const cargar = async () => {
      setCargando(true);
      setError('');
      try {
        // Todos los usuarios, sin los filtros de la grilla.
        const res = await postData('UsuarioListar', { nombre: '', email: '' });
        if (!cancelado) setUsuarios(Array.isArray(res?.value) ? res.value : []);
      } catch (ex) {
        if (!cancelado) setError(ex?.detail || ex?.message || 'No se pudo obtener el listado de usuarios');
      } finally {
        if (!cancelado) setCargando(false);
      }
    };
    cargar();
    return () => {
      cancelado = true;
    };
  }, [open]);

  // El gestor testigo no inicia sesión: su contraseña no vence. Se ordena por vencimiento, los más próximos primero.
  const { filas, rojos, amarillos } = useMemo(() => {
    const ahora = new Date();
    const todas = usuarios
      .filter((u) => !u.esTestigo && u.fechaCambioPass && !Number.isNaN(new Date(u.fechaCambioPass).getTime()))
      .map((u) => {
        const fecha = new Date(u.fechaCambioPass);
        const dias = diasParaVencer(fecha, ahora);
        return { usuario: u, fecha, dias, nivel: nivelDe(dias), texto: textoVencimiento(fecha, dias, ahora) };
      })
      .sort((a, b) => a.fecha - b.fecha);
    return {
      filas: soloConAviso ? todas.filter((f) => f.nivel) : todas,
      rojos: todas.filter((f) => f.nivel === 'urgente').length,
      amarillos: todas.filter((f) => f.nivel === 'aviso').length,
    };
  }, [usuarios, soloConAviso]);

  const cabecera = (texto, ancho) => (
    <TableCell sx={ancho ? { width: ancho } : undefined}>
      <MDTypography variant="caption" fontWeight="bold" color="text">
        {texto}
      </MDTypography>
    </TableCell>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Vencimiento de Claves</DialogTitle>
      <DialogContent dividers>
        <MDBox display="flex" flexDirection="column" gap={2}>
          <MDTypography variant="caption" color="text">
            Es la fecha en que vence la contraseña de cada usuario: pasada esa fecha, el sistema le obliga a cambiarla al
            ingresar. Se ordena por vencimiento, los más próximos primero, para avisar a tiempo.
          </MDTypography>

          <MDBox display="flex" alignItems="center" flexWrap="wrap" gap={3}>
            <MDBox display="flex" alignItems="center" gap={0.5}>
              <ErrorIcon fontSize="small" sx={{ color: COLOR_ROJO }} />
              <MDTypography variant="caption" fontWeight="bold" color="dark">
                {rojos} vencidas o con menos de {DIAS_URGENTE} días
              </MDTypography>
            </MDBox>
            <MDBox display="flex" alignItems="center" gap={0.5}>
              <Warning fontSize="small" sx={{ color: COLOR_AMARILLO }} />
              <MDTypography variant="caption" fontWeight="bold" color="dark">
                {amarillos} con menos de {DIAS_AVISO} días
              </MDTypography>
            </MDBox>
            <FormControlLabel
              sx={{ ml: 'auto' }}
              control={<Switch checked={soloConAviso} onChange={(e) => setSoloConAviso(e.target.checked)} />}
              label={
                <MDTypography variant="caption" fontWeight="regular" color="text">
                  Mostrar solo los que tienen aviso
                </MDTypography>
              }
            />
          </MDBox>

          {error && <Alert severity="error">{error}</Alert>}

          {cargando ? (
            <MDBox display="flex" justifyContent="center" py={4}>
              <CircularProgress size={28} />
            </MDBox>
          ) : (
            <TableContainer sx={{ maxHeight: '55vh' }}>
              <Table size="small" stickyHeader>
                <TableHead sx={{ display: 'table-header-group', p: 0 }}>
                  <TableRow>
                    {cabecera('Usuario')}
                    {cabecera('Fecha de cambio de contraseña', '28%')}
                    {cabecera('Intentos fallidos', '12%')}
                    {cabecera('Activo', '12%')}
                    {cabecera('Email confirmado', '14%')}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filas.map(({ usuario, fecha, nivel, texto }) => (
                    <TableRow key={usuario.idUsuario} hover>
                      <TableCell>
                        <MDBox lineHeight={1.2}>
                          <MDTypography display="block" variant="caption" fontWeight="bold" color="dark">
                            {usuario.email}
                          </MDTypography>
                          <MDTypography display="block" variant="caption" fontWeight="light" color="text">
                            {usuario.nombre}
                          </MDTypography>
                        </MDBox>
                      </TableCell>
                      <TableCell>
                        <MDBox display="flex" alignItems="center" gap={1}>
                          {nivel && (
                            <Tooltip title={texto}>
                              {nivel === 'urgente' ? (
                                <ErrorIcon aria-label={texto} sx={{ color: COLOR_ROJO }} />
                              ) : (
                                <Warning aria-label={texto} sx={{ color: COLOR_AMARILLO }} />
                              )}
                            </Tooltip>
                          )}
                          <MDBox lineHeight={1.2}>
                            <MDTypography display="block" variant="caption" fontWeight="bold" color="dark">
                              {formatearFecha(fecha)}
                            </MDTypography>
                            <MDTypography
                              display="block"
                              variant="caption"
                              fontWeight={nivel ? 'bold' : 'light'}
                              sx={nivel ? { color: nivel === 'urgente' ? COLOR_ROJO : COLOR_AMARILLO_TEXTO } : undefined}
                              color="text"
                            >
                              {texto}
                            </MDTypography>
                          </MDBox>
                        </MDBox>
                      </TableCell>
                      <TableCell>
                        <MDTypography variant="caption" fontWeight="bold" color={colorIntentos(usuario.intentosFallidos)}>
                          {usuario.intentosFallidos}
                        </MDTypography>
                      </TableCell>
                      <TableCell>
                        <MDBox ml={-1}>
                          <MDBadge
                            badgeContent={usuario.activo ? 'activo' : 'desactivado'}
                            color={usuario.activo ? 'success' : 'error'}
                            variant="gradient"
                            size="sm"
                          />
                        </MDBox>
                      </TableCell>
                      <TableCell>
                        {usuario.emailConfirmado ? <CheckCircle color="success" /> : <Cancel color="error" />}
                      </TableCell>
                    </TableRow>
                  ))}
                  {filas.length === 0 && !error && (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <MDTypography variant="button" color="text">
                          {soloConAviso ? `Ninguna contraseña vence en menos de ${DIAS_AVISO} días.` : 'No hay usuarios para mostrar.'}
                        </MDTypography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </MDBox>
      </DialogContent>
      <DialogActions>
        <MDButton variant="outlined" color="info" onClick={onClose}>
          Cerrar
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

export default UsuarioVencimientoClaves;
